import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Menu,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconClockX,
  IconDotsVertical,
  IconEye,
  IconPlus,
  IconSend,
  IconTrash,
} from "@tabler/icons-react";
import { notifyError, notifySuccess, notifyWarning } from "../../utils/notify";
import {
  AssignFormPayload,
  ClientFormStatus,
  ClientFormSummary,
  FormTemplate,
  SendFormLinkResponse,
} from "../../types/formTemplates";
import { TemplateLibraryModal } from "../formTemplates/TemplateLibraryModal";
import { FormBuilder } from "../formTemplates/builder/FormBuilder";
import { FormViewer } from "../formTemplates/viewer/FormViewer";
import { ClientFormsListSection } from "../formTemplates/ClientFormsListSection";
import { useFormTemplates } from "../../context/FormTemplatesContext";
import { useClientForms } from "../../context/ClientFormsContext";
import usePermissions from "../../hooks/usePermissions";
import primaryAction from "../common/primaryFilledButton.module.css";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import styles from "./ClientFormsWaiversTab.module.css";

interface ClientFormsWaiversTabProps {
  salonId: string;
  clientId: string;
  clientDisplayName: string;
  clientPhone?: string | null;
  clientOptedInReminder?: boolean | null;
  readOnly?: boolean;
}

type BuilderState =
  | { mode: "closed" }
  | { mode: "create"; seed?: FormTemplate; cloneFromSlug?: string }
  | { mode: "edit"; template: FormTemplate };

type ViewerState = { mode: "closed" } | { mode: "open"; formId: string };

function normalizeClientFormsList(value: unknown): ClientFormSummary[] {
  if (Array.isArray(value)) return value;

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;

    if (Array.isArray(record.forms)) return record.forms as ClientFormSummary[];
    if (Array.isArray(record.client_forms))
      return record.client_forms as ClientFormSummary[];
    if (Array.isArray(record.items)) return record.items as ClientFormSummary[];
    if (
      record.data &&
      typeof record.data === "object" &&
      Array.isArray((record.data as Record<string, unknown>).forms)
    ) {
      return (record.data as Record<string, unknown>)
        .forms as ClientFormSummary[];
    }
  }

  return [];
}

export const ClientFormsWaiversTab: React.FC<ClientFormsWaiversTabProps> = ({
  salonId,
  clientId,
  clientDisplayName,
  clientPhone,
  clientOptedInReminder,
  readOnly = false,
}) => {
  const { fetchTemplateById } = useFormTemplates();
  const {
    listClientForms,
    assignForm,
    deleteClientForm,
    expireClientForm,
    sendFormLink,
  } = useClientForms();
  const perms = usePermissions();
  const canManageTemplates =
    perms?.formsAndWaivers?.canManageTemplates !== false;
  const canManageClientForms = !readOnly;

  const [libraryOpen, setLibraryOpen] = useState(false);
  const [builder, setBuilder] = useState<BuilderState>({ mode: "closed" });
  const [viewer, setViewer] = useState<ViewerState>({ mode: "closed" });
  const [forms, setForms] = useState<ClientFormSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ClientFormSummary | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingExpire, setPendingExpire] = useState<ClientFormSummary | null>(
    null,
  );
  const [isExpiring, setIsExpiring] = useState(false);
  const [pendingSend, setPendingSend] = useState<ClientFormSummary | null>(
    null,
  );
  const [isSending, setIsSending] = useState(false);

  const hasPhone = Boolean(clientPhone);
  // Treat undefined/null (value not yet loaded) as opted-in: the backend is
  // authoritative on the actual send and returns reason:"opted_out" if not,
  // so we don't block the menu item before the client detail finishes loading.
  const isOptedIn = clientOptedInReminder !== false;
  const sendDisabledReason = !hasPhone
    ? "Client has no phone number on file"
    : !isOptedIn
      ? "Client has opted out of reminders"
      : null;

  const refresh = useCallback(async () => {
    if (!clientId) return;
    setIsLoading(true);
    try {
      const list = await listClientForms(clientId);
      // SOAP notes (category='per_visit_clinical') live on their own
      // dedicated tab (one-instance-per-appointment, never reused), so they
      // are filtered out here. Forms & Waivers stays the home for reusable
      // intake/consent + per-visit forms.
      setForms(
        normalizeClientFormsList(list).filter(
          (f) => f.category !== "per_visit_clinical",
        ),
      );
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not load forms",
      });
    } finally {
      setIsLoading(false);
    }
  }, [clientId, listClientForms]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAssign = async (payload: AssignFormPayload): Promise<boolean> => {
    setIsAssigning(true);
    try {
      const assigned = await assignForm(clientId, payload);
      notifySuccess(`${assigned.name} is now on this client.`, {
        title: "Form assigned",
      });
      await refresh();
      setViewer({ mode: "open", formId: assigned.id });
      return true;
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not assign form",
      });
      return false;
    } finally {
      setIsAssigning(false);
    }
  };

  const handleExpire = (form: ClientFormSummary) => {
    // Mirror the menu-item's state-aware predicate so awaiting_staff /
    // awaiting_client forms (where is_submitted=false but state != draft)
    // can be expired — Expire is the universal post-submit invalidation
    // workflow.
    const isDraft = form.state ? form.state === "draft" : !form.is_submitted;
    if (isDraft) return;
    setPendingExpire(form);
  };

  const confirmExpire = async () => {
    if (!pendingExpire) return;
    setIsExpiring(true);
    try {
      await expireClientForm(clientId, pendingExpire.id);
      notifySuccess(`${pendingExpire.name} is now marked expired.`, {
        title: "Form expired",
      });
      setPendingExpire(null);
      await refresh();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not expire form",
      });
    } finally {
      setIsExpiring(false);
    }
  };

  const confirmSend = async () => {
    if (!pendingSend?.transaction_uuid) return;
    setIsSending(true);
    try {
      const result: SendFormLinkResponse = await sendFormLink(
        pendingSend.transaction_uuid,
      );
      if (result.sent) {
        notifySuccess(`SMS sent to ${clientDisplayName}.`, {
          title: "Form link sent",
        });
      } else if (result.reason === "no_contact") {
        notifyWarning("Client has no phone number on file.", {
          title: "Could not send",
        });
      } else if (result.reason === "opted_out") {
        notifyWarning("Client has opted out of reminders.", {
          title: "Could not send",
        });
      }
      setPendingSend(null);
      await refresh();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not send form link",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleDelete = (form: ClientFormSummary) => {
    // Mirror the menu-item's state-aware predicate so a future state value
    // can't slip through (e.g., awaiting_client where is_submitted=false).
    // safety — button is hidden anyway.
    const isDraft = form.state ? form.state === "draft" : !form.is_submitted;
    if (!isDraft) return;
    setPendingDelete(form);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await deleteClientForm(clientId, pendingDelete.id);
      notifySuccess(`Removed ${pendingDelete.name}.`, {
        title: "Form deleted",
      });
      setPendingDelete(null);
      await refresh();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not delete form",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditCustom = async (template: FormTemplate) => {
    setLibraryOpen(false);
    try {
      const detail = await fetchTemplateById(template.id);
      if (detail) {
        setBuilder({ mode: "edit", template: detail });
      }
    } catch {
      notifyError("Failed to load the template to edit. Try again.", {
        title: "Could not load template",
      });
      setLibraryOpen(true);
    }
  };

  const handleCreateNew = () => {
    setLibraryOpen(false);
    setBuilder({ mode: "create" });
  };

  const handleUseTemplate = async (template: FormTemplate) => {
    const ok = await handleAssign({
      source_type: template.source === "custom" ? "salon_template" : "template",
      source_id: template.slug || template.id,
    });
    if (ok) setLibraryOpen(false);
  };

  return (
    <Box className={styles.shell}>
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
        <Stack gap={4}>
          <Title order={2} className={styles.title}>
            Forms &amp; Waivers
          </Title>
          <Text className={styles.subtitle}>
            Manage intake forms, consent forms, and waivers.
          </Text>
        </Stack>
        <Button
          variant="filled"
          className={primaryAction.primaryFilled}
          leftSection={<IconPlus size={16} stroke={1.75} />}
          onClick={() => setLibraryOpen(true)}
          disabled={readOnly}
        >
          Add Form/Waiver
        </Button>
      </Group>

      <ClientFormsListSection
        forms={forms}
        isLoading={isLoading}
        density="comfortable"
        emptyTitle="No forms assigned yet"
        emptyDescription="Click Add Form/Waiver to give this client an intake, waiver, or note."
        onOpenForm={(f) => setViewer({ mode: "open", formId: f.id })}
        renderRowActions={(f) => (
          <Menu position="bottom-end" withinPortal shadow="md">
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={`Actions for ${f.name}`}
              >
                <IconDotsVertical size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEye size={14} />}
                onClick={() => setViewer({ mode: "open", formId: f.id })}
              >
                View form
              </Menu.Item>
              {process.env.REACT_APP_MED_SPA_FORM_NOTIFICATION === "true" &&
                f.transaction_uuid &&
                !f.is_submitted &&
                !readOnly && (
                  <Tooltip
                    label={sendDisabledReason}
                    disabled={!sendDisabledReason}
                    withArrow
                    position="left"
                  >
                    <Menu.Item
                      leftSection={<IconSend size={14} />}
                      disabled={!!sendDisabledReason}
                      onClick={() => setPendingSend(f)}
                    >
                      Send to client
                    </Menu.Item>
                  </Tooltip>
                )}
              {(f.state ? f.state !== "draft" : f.is_submitted) &&
                f.status !== "expired" &&
                f.status !== "past_expiry" &&
                !readOnly && (
                  <Menu.Item
                    leftSection={<IconClockX size={14} />}
                    onClick={() => handleExpire(f)}
                  >
                    Set expire date
                  </Menu.Item>
                )}
              {(f.state ? f.state === "draft" : !f.is_submitted) &&
                !readOnly && (
                  <>
                    <Menu.Divider />
                    <Menu.Item
                      color="red"
                      leftSection={<IconTrash size={14} />}
                      onClick={() => handleDelete(f)}
                    >
                      Delete
                    </Menu.Item>
                  </>
                )}
            </Menu.Dropdown>
          </Menu>
        )}
      />

      <TemplateLibraryModal
        opened={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onUseTemplate={handleUseTemplate}
        onEditCustom={handleEditCustom}
        onCreateNew={handleCreateNew}
        canManageTemplates={canManageTemplates}
        canManageClientForms={canManageClientForms}
        isAssigning={isAssigning}
      />

      {builder.mode !== "closed" && (
        <FormBuilder
          mode={builder.mode}
          initialTemplate={
            builder.mode === "edit"
              ? builder.template
              : builder.mode === "create"
                ? builder.seed
                : undefined
          }
          cloneFromSlug={
            builder.mode === "create" ? builder.cloneFromSlug : undefined
          }
          onClose={() => setBuilder({ mode: "closed" })}
          onSaved={() => setBuilder({ mode: "closed" })}
        />
      )}

      {viewer.mode === "open" && (
        <FormViewer
          salonId={salonId}
          clientId={clientId}
          formId={viewer.formId}
          onClose={() => setViewer({ mode: "closed" })}
          onChanged={() => {
            refresh();
          }}
        />
      )}

      <DeleteConfirmationModal
        isOpen={pendingDelete !== null}
        onClose={() => {
          if (!isDeleting) setPendingDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete form"
        message={
          pendingDelete
            ? `Delete "${pendingDelete.name}"?\n\nThis cannot be undone.`
            : ""
        }
        isLoading={isDeleting}
      />

      <DeleteConfirmationModal
        isOpen={pendingExpire !== null}
        onClose={() => {
          if (!isExpiring) setPendingExpire(null);
        }}
        onConfirm={confirmExpire}
        title="Expire form"
        message={
          pendingExpire
            ? `Expire "${pendingExpire.name}"?\n\nThis sets the expiration date to now and cannot be undone.`
            : ""
        }
        isLoading={isExpiring}
        confirmLabel="Expire"
        confirmColor="orange"
      />

      <DeleteConfirmationModal
        isOpen={pendingSend !== null}
        onClose={() => {
          if (!isSending) setPendingSend(null);
        }}
        onConfirm={confirmSend}
        title="Send form link"
        message={
          pendingSend
            ? `Send a form link to ${clientDisplayName} for "${pendingSend.name}"?`
            : ""
        }
        isLoading={isSending}
        confirmLabel="Send"
        confirmColor="teal"
      />
    </Box>
  );
};

export default ClientFormsWaiversTab;
